import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FullWidth } from 'ui/utility/fullWidth';
import { HeaderTitle } from 'ui/pageHeader';
import { ModalContextProvider } from 'utils/contexts/modal';
import { SortingContextProvider, useSortingContext } from 'utils/contexts/sorting';

import { IndexPageContainer, StyledPageHeader } from './IndexPage.styled';
import { Table } from './table/Table';

import { GeneralEmptyState, NoResults } from './IndexEmptyState';
import { IndexHeaderActions } from './IndexHeaderActions';
import { fetchDataFeedsRequest } from '../actions';
import { TableContextProvider } from '../contexts/TableProvider';
import { useTableContext } from '../contexts/Table';
import { FilterContextProvider } from '../contexts/FilterProvider';
import { useFilterContext } from '../contexts/Filter';
import { useTranslations } from '../hooks/useTranslations';
import { getDataFeeds, getIsDataFetching } from '../selectors';
import { DataFeed, TABLE_COLUMN_ID } from '../types';
import { filterRows, sortRows } from '../utils';
import { Modals } from './modals/Modals';

export const testIds = {
  container: 'df-index-container',
  header: 'df-index-header',
};

export const IndexPage = () => {
  const { t } = useTranslations();
  const { sortBy, sortDirection } = useSortingContext();
  const { searchQuery } = useFilterContext();
  const { columns } = useTableContext();
  /**
   * --Lifecycle--
   * Fetch data feeds upon page mount
   */
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchDataFeedsAction = fetchDataFeedsRequest();
    dispatch(fetchDataFeedsAction);
  }, []);

  /**
   * -- Selector Data --
   */
  const dataFeeds = useSelector(getDataFeeds);
  const isDataFetching = useSelector(getIsDataFetching);

  const totalFeedCount = Object.keys(dataFeeds).length;

  /**
   * -- Render Logic --
   * configuredColumns - the table column data with the appropriate handlers passed in
   * activeRows - the rows to display after running the filter
   * sortedRows - the active rows with chosen sort applied
   */
  const activeRows = filterRows(columns, Object.values(dataFeeds), searchQuery) as DataFeed[];
  const sortedRows = sortRows(activeRows, sortBy as keyof DataFeed, sortDirection) as DataFeed[];

  return (
    <IndexPageContainer data-test={testIds.container}>
      <StyledPageHeader
        data-test={testIds.header}
        documentTitle={t('header.dataFeeds')}
        pageActions={<IndexHeaderActions />}
      >
        <HeaderTitle>{t('header.dataFeeds')}</HeaderTitle>
      </StyledPageHeader>

      <FullWidth>
        {(isDataFetching || !!totalFeedCount) && <Table rowData={sortedRows} />}
        {!isDataFetching &&
          !totalFeedCount &&
          (searchQuery ? <NoResults /> : <GeneralEmptyState />)}
      </FullWidth>

      <Modals />
    </IndexPageContainer>
  );
};

const IndexPageContext = () => (
  <SortingContextProvider defaultSortField={TABLE_COLUMN_ID.ID}>
    <FilterContextProvider>
      <TableContextProvider>
        <ModalContextProvider>
          <IndexPage />
        </ModalContextProvider>
      </TableContextProvider>
    </FilterContextProvider>
  </SortingContextProvider>
);

export default IndexPageContext;
